import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkedRef = { current: false };

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        setUser(await res.json());
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    // Prevent double-call from StrictMode
    if (checkedRef.current) return;
    checkedRef.current = true;
    checkAuth();
  }, [checkAuth]);

  // Email/password login
  const loginWithEmail = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error('Login failed - invalid response');
    }
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    setUser(data.user);
    // Refresh from /auth/me so the user object carries `subscription_status`
    // BEFORE any post-login navigation — the paywall in ProtectedRoute needs
    // it to decide whether to redirect to /billing/plans.
    await checkAuth();
    return data;
  };

  // Email/password register
  const register = async (name, email, password) => {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, password }),
    });
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error('Registration failed - invalid response');
    }
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    setUser(data.user);
    // See loginWithEmail — refresh so `subscription_status` is on `user`
    // before AuthPage navigates to /billing/plans.
    await checkAuth();
    return data;
  };

  // Google OAuth - redirect to Google
  const loginWithGoogle = () => {
    const redirectUri = window.location.origin + '/auth/callback';
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  // Exchange Google code for session
  const exchangeGoogleCode = async (code) => {
    const redirectUri = window.location.origin + '/auth/callback';
    const res = await fetch(`${API}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Google sign-in failed');
    setUser(data.user);
    // See loginWithEmail — refresh so `subscription_status` is on `user`.
    await checkAuth();
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    setUser(null);
  };

  const setUserData = (data) => setUser(data);

  return (
    <AuthContext.Provider value={{
      user, loading, logout, checkAuth, setUserData,
      loginWithEmail, register, loginWithGoogle, exchangeGoogleCode,
      // Keep legacy `login` pointing to email auth page
      login: () => { window.location.href = '/auth'; },
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
